import { useState, useEffect, useRef, useCallback } from "react";
import { useIntl } from "react-intl";
import { Eye, Edit, Plus, CircleX } from "lucide-react";
import Message from "../../../../../../i18n/Message";
import label from "../../../../../../i18n/label";
import backend from "../../../../../../backend/backend";
import Notify from "../../../../../shared/Notify";
import Spinner from "../../../../../../assets/icons/Spinner";
import ChevronLeftIcon from "../../../../../../assets/icons/ChevronLeftIcon";
import CheckIcon from "../../../../../../assets/icons/CheckIcon";
import XIcon from "../../../../../../assets/icons/CrossIcon";
import Tooltip from "../../../../../shared/Tooltip";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import RefreshCwIcon from "../../../../../../assets/icons/RefreshCwIcon";
import "./ConfluenceContentPanel.scss";

dayjs.extend(relativeTime);

const ConfluenceContentPanel = ({
  kbase,
  workspaceId,
  integrationConfig,
  onBack,
}) => {
  const intl = useIntl();
  const [pageUrl, setPageUrl] = useState("");
  const [autoSync, setAutoSync] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const pollingTimerRef = useRef(null);

  const parseMetadata = (metadataJson) => {
    if (!metadataJson) return null;
    try {
      return JSON.parse(metadataJson);
    } catch (e) {
      console.error("Failed to parse integration metadata", e);
      return null;
    }
  };

  // Load initial config
  useEffect(() => {
    const metadata = parseMetadata(integrationConfig?.integrationMetadata);
    if (metadata) {
      setPageUrl(metadata.pageUrl || "");
      setAutoSync(metadata.autoSync !== false);
    }
  }, [integrationConfig]);

  // Load sync status
  const loadSyncStatus = useCallback(() => {
    backend
      .getKBaseSyncStatus(workspaceId, kbase.uid)
      .then((data) => {
        setSyncStatus(data);
        setLoadingStatus(false);
      })
      .catch((err) => {
        console.error("Failed to load sync status", err);
        setLoadingStatus(false);
      });
  }, [workspaceId, kbase]);

  // Load sync history
  const loadSyncHistory = useCallback(() => {
    backend
      .getKBaseSyncHistory(workspaceId, kbase.uid)
      .then((data) => {
        setSyncHistory(data || []);
        setLoadingHistory(false);

        // Check if any sync is in progress
        const hasInProgress = data?.some(
          (log) => log.status === "IN_PROGRESS" || log.status === "PENDING"
        );
        setSyncing(hasInProgress);
      })
      .catch((err) => {
        console.error("Failed to load sync history", err);
        setLoadingHistory(false);
      });
  }, [workspaceId, kbase]);

  useEffect(() => {
    loadSyncStatus();
    loadSyncHistory();
  }, [kbase, workspaceId, loadSyncStatus, loadSyncHistory]);

  // Polling effect
  useEffect(() => {
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }

    if (syncing) {
      pollingTimerRef.current = setTimeout(() => {
        loadSyncStatus();
        loadSyncHistory();
      }, 5000);
    }

    return () => {
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
      }
    };
  }, [syncing, loadSyncStatus, loadSyncHistory]);

  const handleSaveConfig = (e) => {
    e.preventDefault();

    setSaving(true);

    const config = {
      integrationConnectionUid: integrationConfig.integrationConnectionUid,
      type: "CONFLUENCE",
      pageUrl: pageUrl,
      autoSync: autoSync,
    };

    backend
      .updateKBaseIntegration(workspaceId, kbase.uid, config)
      .then(() => {
        Notify.success(label(intl, "kbase_integration_config_saved"));
        setSaving(false);
      })
      .catch((err) => {
        console.error("Failed to save configuration", err);
        Notify.error(label(intl, "error_save_integration_config"));
        setSaving(false);
      });
  };

  const handleSyncNow = () => {
    setSyncing(true);
    backend
      .triggerKBaseSync(workspaceId, kbase.uid)
      .then(() => {
        Notify.success(label(intl, "kbase_integration_sync_triggered"));
        // Start polling immediately
        setTimeout(() => {
          loadSyncStatus();
          loadSyncHistory();
        }, 1000);
      })
      .catch((err) => {
        console.error("Failed to trigger sync", err);
        Notify.error(label(intl, "error_trigger_sync"));
        setSyncing(false);
      });
  };

  const getSyncStatusBadge = () => {
    if (!syncHistory || syncHistory.length === 0) {
      return (
        <span className="status_badge idle">
          <Message id="kbase_integration_idle" />
        </span>
      );
    }

    const latestLog = syncHistory[0];
    if (latestLog.status === "IN_PROGRESS" || latestLog.status === "PENDING") {
      return (
        <span className="status_badge in_progress">
          <Spinner size={14} />
          <Message id="kbase_integration_syncing" />
        </span>
      );
    } else if (latestLog.status === "COMPLETED") {
      return (
        <span className="status_badge completed">
          <CheckIcon size={14} color="#28a745" />
          <Message id="kbase_integration_status_completed" />
        </span>
      );
    } else if (latestLog.status === "FAILED") {
      return (
        <span className="status_badge failed">
          <XIcon size={14} color="#dc3545" />
          <Message id="kbase_integration_status_failed" />
        </span>
      );
    }

    return (
      <span className="status_badge idle">
        <Message id="kbase_integration_idle" />
      </span>
    );
  };

  const formatDuration = (ms) => {
    if (!ms) return "-";
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "COMPLETED":
        return "status_badge completed";
      case "FAILED":
        return "status_badge failed";
      case "IN_PROGRESS":
        return "status_badge in_progress";
      case "PENDING":
        return "status_badge pending";
      default:
        return "status_badge";
    }
  };

  return (
    <div className="confluence_content_panel">
      <div className="back_button" onClick={onBack}>
        <ChevronLeftIcon size={16} strokeWidth="2" color="#67748e" />
        <Message id="kbase_integration_back" />
      </div>

      {/* Configuration Section */}
      <div className="panel_section">
        <h3 className="section_title">Configuration</h3>
        <form onSubmit={handleSaveConfig}>
          <div className="form_group">
            <label>
              <Message id="kbase_integration_page_url" />
            </label>
            <input
              className="form-control"
              type="url"
              required
              value={pageUrl}
              onChange={(e) => setPageUrl(e.target.value)}
              readOnly={saving}
            />
          </div>

          <div className="form_group checkbox_group">
            <label>
              <input
                type="checkbox"
                checked={autoSync}
                onChange={(e) => setAutoSync(e.target.checked)}
                disabled={saving}
              />
              <Message id="kbase_integration_auto_sync" />
            </label>
          </div>

          {/*<div className="form_group">
            <label>
              <Message id="kbase_integration_sync_frequency" />
            </label>
            <select className="form-control" disabled>
              <option>Daily</option>
            </select>
          </div>
          */}

          <button
            className="btn btn-outline-dark btn-sm"
            type="submit"
            disabled={saving}
          >
            {saving ? (
              <Spinner size={16} />
            ) : (
              <Message id="kbase_integration_save_changes" />
            )}
          </button>
        </form>
      </div>

      {/* Sync Status Section */}
      <div className="panel_section">
        <h3 className="section_title">
          <Message id="kbase_integration_sync_status" />
        </h3>
        {loadingStatus && <Spinner size={24} />}
        {!loadingStatus && (
          <div className="sync_status_content">
            <div className="status_row">
              <span className="status_label">Status:</span>
              {getSyncStatusBadge()}
            </div>
            {integrationConfig?.lastSyncAt && (
              <div className="status_row">
                <span className="status_label">
                  <Message id="kbase_integration_last_sync" />:
                </span>
                <span className="status_value">
                  {dayjs(integrationConfig.lastSyncAt).fromNow()}
                </span>
              </div>
            )}
            {syncStatus && (
              <div className="status_row">
                <span className="status_label">Pages:</span>
                <span className="status_value">
                  {syncStatus.synced}{" "}
                  <Message id="kbase_integration_pages_synced" />,{" "}
                  {syncStatus.pending}{" "}
                  <Message id="kbase_integration_pages_pending" />,{" "}
                  {syncStatus.failed}{" "}
                  <Message id="kbase_integration_pages_failed" />
                </span>
              </div>
            )}
            <div className="status_row">
              <button
                className="btn btn-outline-dark btn-sm"
                onClick={handleSyncNow}
                disabled={syncing}
              >
                {syncing ? (
                  <>
                    <Spinner size={16} />
                    <Message id="kbase_integration_syncing" />
                  </>
                ) : (
                  <>
                    <RefreshCwIcon size={16} color="#344767" />
                    <Message id="kbase_integration_sync_now" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sync History Section */}
      <div className="panel_section">
        <h3 className="section_title">
          <Message id="kbase_integration_recent_history" />
        </h3>
        {loadingHistory && <Spinner size={24} />}
        {!loadingHistory && syncHistory.length === 0 && (
          <div className="empty_history">
            <Message id="kbase_integration_history_empty" />
          </div>
        )}
        {!loadingHistory && syncHistory.length > 0 && (
          <div className="sync_history_table">
            <table>
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Status</th>
                  <th>Duration</th>
                  <th>Pages</th>
                </tr>
              </thead>
              <tbody>
                {syncHistory.map((log) => (
                  <tr key={log.uid}>
                    <td>{dayjs(log.startedAt).format("MMM D, h:mm A")}</td>
                    <td>
                      <span className={getStatusBadgeClass(log.status)}>
                        {log.status}
                      </span>
                    </td>
                    <td>{formatDuration(log.durationMs)}</td>
                    <td>
                      <div className="pages_stats">
                        <Tooltip
                          content={`${log.pagesProcessed || 0} pages processed`}
                        >
                          <span className="stat_item neutral">
                            <Eye size={14} color="#4a90e2" />
                            <span className="stat_count">
                              {log.pagesProcessed || 0}
                            </span>
                          </span>
                        </Tooltip>
                        <Tooltip
                          content={`${log.pagesCreated || 0} pages created`}
                        >
                          <span className="stat_item success">
                            <Plus size={14} color="#28a745" />
                            <span className="stat_count">
                              {log.pagesCreated || 0}
                            </span>
                          </span>
                        </Tooltip>
                        <Tooltip
                          content={`${log.pagesUpdated || 0} pages updated`}
                        >
                          <span className="stat_item success">
                            <Edit size={14} color="#28a745" />
                            <span className="stat_count">
                              {log.pagesUpdated || 0}
                            </span>
                          </span>
                        </Tooltip>
                        <Tooltip
                          content={`${log.pagesFailed || 0} pages failed`}
                        >
                          <span className="stat_item danger">
                            <CircleX size={14} color="#dc3545" />
                            <span className="stat_count">
                              {log.pagesFailed || 0}
                            </span>
                          </span>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfluenceContentPanel;
